import { gql, ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";

const ARENA_GRAPHQL_URI =
  (import.meta as any).env?.VITE_ARENA_GRAPHQL_URI ||
  ((import.meta as any).env?.DEV
    ? "http://localhost:4001/graphql"
    : "https://backend-dev-api.backend.perpx.ai/graphql");

const httpLink = createHttpLink({
  uri: ARENA_GRAPHQL_URI,
});

export const arenaClient = new ApolloClient({
  link: httpLink,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: { fetchPolicy: "cache-and-network" },
  },
});

export const GET_ARENA_BLOCK_STATUS = gql`
  query ArenaBlockStatus {
    arenaBlockStatus {
      blocked
      raffleEnabled
      season
      lastEndedSeason {
        seasonId
        label
        endsAt
      }
    }
  }
`;

export type ArenaBlockStatus = {
  blocked: boolean;
  raffleEnabled: boolean;
  season: string;
  lastEndedSeason: {
    seasonId: string;
    label: string;
    endsAt: string;
  } | null;
};

export const GET_ARENA_STATE = gql`
  query ArenaUserState($address: String!) {
    arenaUserState(address: $address) {
      address
      season
      totalXP
      level
      tier
      xpIntoLevel
      xpPerLevel
      letters
      word
      wordCompletedCount
      raffleTickets
      raffleCreditUsdc
    }
  }
`;

export const GET_ARENA_QUESTS = gql`
  query ArenaQuests($address: String!) {
    arenaQuests(address: $address) {
      key
      kind
      title
      description
      reward {
        xp
        raffleTickets
        letters
      }
      target {
        volumeUsd
        pnlUsd
        tweetUrl
        telegramUrl
      }
      status
      progress
      lastClaimedAt
    }
  }
`;

export const GET_TWITTER_LINKED = gql`
  query ArenaTwitterLinked($address: String!) {
    arenaTwitterLinked(address: $address) {
      connected
      username
    }
  }
`;

export const GET_TELEGRAM_LINKED = gql`
  query ArenaTelegramLinked($address: String!) {
    arenaTelegramLinked(address: $address)
  }
`;

export const CLAIM_QUEST = gql`
  mutation ArenaClaimQuest($address: String!, $questKey: String!, $ctx: String) {
    arenaClaimQuest(address: $address, questKey: $questKey, ctx: $ctx) {
      address
      totalXP
      level
      tier
      xpIntoLevel
      xpPerLevel
      letters
      wordCompletedCount
      raffleTickets
      raffleCreditUsdc
    }
  }
`;

export const SPIN_RAFFLE = gql`
  mutation ArenaSpinRaffle($address: String!) {
    arenaSpinRaffle(address: $address) {
      segmentIndex
      prizeUsdc
      wheel
      drawId
      state {
        totalXP
        level
        raffleTickets
        raffleCreditUsdc
      }
    }
  }
`;

export const GET_ARENA_BATCH = gql`
  query ArenaBatchStates($addresses: [String!]!) {
    arenaBatchStates(addresses: $addresses) {
      address
      level
      tier
      letters
      raffleTickets
    }
  }
`;

export type ArenaBatchState = {
  address: string;
  level: number;
  tier: number;
  letters: string[];
  raffleTickets: number;
};

export const TRACK_EVENT = gql`
  mutation ArenaTrackEvent($address: String, $eventType: String!) {
    arenaTrackEvent(address: $address, eventType: $eventType)
  }
`;

export type ArenaUserState = {
  address: string;
  season: string;
  totalXP: number;
  level: number;
  tier: number;
  xpIntoLevel: number;
  xpPerLevel: number;
  letters: string[];
  word: string;
  wordCompletedCount: number;
  raffleTickets: number;
  raffleCreditUsdc: number;
};

export type ArenaQuest = {
  key: string;
  kind:
    | "DAILY_CHECKIN"
    | "SOCIAL_TWITTER"
    | "SOCIAL_TELEGRAM"
    | "TIME_ATTACK"
    | "LEVEL_UP"
    | "STAKING";
  title: string;
  description: string;
  reward: { xp: number; raffleTickets?: number; letters?: string[] };
  target?: {
    volumeUsd?: number;
    pnlUsd?: number;
    tweetUrl?: string;
    telegramUrl?: string;
  };
  status: "PENDING" | "VERIFYING" | "CLAIMABLE" | "CLAIMED" | "FAILED";
  progress: number;
  lastClaimedAt: string | null;
};
